import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {apiURI, mainSite} from '../config.js';
import Bugsnag from '@bugsnag/expo';
import {PivotLogging} from './cbcLoggingSlice.js';
import {useDispatch} from 'react-redux';

/** STATE OBJECT */
const initialState = {
    loading_msg: 'idle',
    insertActivityLogResultsMsg: '',
    updateActivityLogResultsMsg: '',
    activity_log_id: 0,
    activity_modal_messaging: null,
    insert_return: [],
    update_return: [],
};

export const insertActivityLog = createAsyncThunk(
    'activityActions/insertActivityLog',
    async (insertLogObject, {rejectWithValue, dispatch}) => {
        Bugsnag.leaveBreadcrumb('Action insertActivityLog useDispatched');
        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `insertActivityLog useDispatched: ${insertLogObject}`
        }));

        const requestBody = {
            activity_id: insertLogObject.activity_id,
            current_user_id: insertLogObject.current_user_id,
            game_type: insertLogObject.game_type,
            assessment_log_id: null,
            workout_id: insertLogObject.workout_id,
        };

        const formBody = Object.keys(requestBody)
            .map(
                (key) =>
                    encodeURIComponent(key) + '=' + encodeURIComponent(requestBody[key])
            )
            .join('&');

        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `Request body: ${JSON.stringify(requestBody)}`
        }));
        dispatch(PivotLogging({moduleName: 'activityActions', consoleLog: `Form body: ${formBody}`}));

        const response = await fetch(`${apiURI}/ahs-insert-activity-log.php`, {
            method: 'POST',
            redirect: 'follow',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                'Access-Control-Allow-Origin': '*',
            },
            body: formBody,
        });

        const results = await response.json();
        console.log('response from activity actions' , response);
        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `insertActivityLog response: ${JSON.stringify(response)}`
        }));
        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `insertActivityLog results: ${JSON.stringify(results)}`
        }));

        if (response.ok) {
            if (!results.success) {
                return rejectWithValue(results.msg);
            }

            return results;
        } else {
            Bugsnag.notify(`Api Error, ahs-insert-activity-log: ${response.text()}`);
            return rejectWithValue(response.text());
        }
    }
);

export const updateActivityLog = createAsyncThunk(
    'activityActions/updateActivityLog',
    async (updateLogObject, {rejectWithValue, dispatch}) => {
        Bugsnag.leaveBreadcrumb('Action updateActivityLog useDispatched');
        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `updateActivityLog useDispatched: ${updateLogObject}`
        }));
        const requestBody = {
            activity_id: updateLogObject.activity_id,
            current_user_id: updateLogObject.current_user_id,
            activity_type: updateLogObject.activity_type,
            assessment_log_id: null,
            activity_log_id: updateLogObject.activity_log_id,
            completed: updateLogObject.completed,
            total_correct: 0,
            total_incorrect: 0,
            score: 0,
            activity_result: 0,
            result_breakdown: '',
            scoring: updateLogObject.scoring,
            total_rounds: updateLogObject.total_rounds,
            score_multiplier: updateLogObject.score_multiplier,
            score_increment: updateLogObject.score_increment,
        };

        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `updateActivityLog request body: ${JSON.stringify(requestBody)}`
        }));

        const formBody = Object.keys(requestBody)
            .map(
                (key) =>
                    encodeURIComponent(key) + '=' + encodeURIComponent(requestBody[key])
            )
            .join('&');

        const response = await fetch(`${apiURI}/ahs-update-activity-log.php`, {
            method: 'POST',
            redirect: 'follow',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                'Access-Control-Allow-Origin': '*',
            },
            body: formBody,
        });

        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `updateActivityLog response: ${JSON.stringify(response)}`
        }));

        const results = await response.json();
        dispatch(PivotLogging({
            moduleName: 'activityActions',
            consoleLog: `updateActivityLog results: ${JSON.stringify(results)}`
        }));

        if (response.ok) {
            if (!results.success) {
                return rejectWithValue(results.msg);
            }
            return results;
        } else {
            Bugsnag.notify(`Api Error, ahs-insert-activity-log: ${response.text()}`);
            return rejectWithValue(response.text());
        }
    }
);

/** DEFINE REDUCERS */
export const cbcActivityActions = createSlice({
    name: 'activityActions',
    initialState,
    reducers: {
        clearActivityStoreModalMessage(state){
            console.log('clearing activity_modal_messaging');
            state.activity_modal_messaging = null;
            console.log('cleared activity_modal_messaging' , state.activity_modal_messaging);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(insertActivityLog.pending, (state) => {
                state.loading_msg = 'pending';
                state.insertActivityLogResultsMsg = 'Attempting to insert activity log';
            })
            .addCase(insertActivityLog.fulfilled, (state, {payload}) => {
                state.loading_msg = 'idle';
                state.insertActivityLogResultsMsg = 'success';
                state.activity_log_id = payload.activity_log_id;
            })
            .addCase(insertActivityLog.rejected, (state, {payload}) => {
                state.loading_msg = 'failed';
                state.insertActivityLogResultsMsg = payload;
            })

            .addCase(updateActivityLog.pending, (state) => {
                state.loading_msg = 'pending';
                state.updateActivityLogResultsMsg = 'Attempting to update activity log';
            })
            .addCase(updateActivityLog.fulfilled, (state, {payload}) => {
                console.log('updateActivityLog.fulfilled' , payload);
                state.loading_msg = 'idle';
                state.updateActivityLogResultsMsg = 'success';
                state.activity_modal_messaging = payload.react_modal_messaging;
            })
            .addCase(updateActivityLog.rejected, (state, {payload}) => {
                state.loading_msg = 'failed';
                state.updateActivityLogResultsMsg = payload;
            });
    },
});

// extract the reducer from the slice
const {reducer, actions} = cbcActivityActions;

export const {clearActivityStoreModalMessage} = actions;

// export the reducer
export default reducer;